import React, { useEffect, useState } from "react";
import { custApi, formatErr } from "../../lib/customerApi";
import { useCustAuth } from "../../lib/customerAuth";
import { toast } from "sonner";
import {
  User, Phone, Envelope, House, MapPin, IdentificationCard, Truck,
  CheckCircle, PencilSimple, FloppyDisk, X, Leaf, ArrowSquareOut,
  Calendar, Tag, Buildings, Heart, Plus, Trash, PencilLine,
} from "@phosphor-icons/react";

const VEHICLE_TYPES = ["", "two_wheeler", "three_wheeler", "four_wheeler", "tempo", "truck", "other"];

export default function MeProfile() {
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const { logout } = useCustAuth();

  const load = async () => {
    try {
      const r = await custApi.get("/me");
      setMe(r.data);
      setForm({
        name: r.data.name || "",
        alternate_phone: r.data.alternate_phone || "",
        email: r.data.email || "",
        address: r.data.address || "",
        landmark: r.data.landmark || "",
        house_no: r.data.house_no || "",
        house_name: r.data.house_name || "",
        haritha_mithram_id: r.data.haritha_mithram_id || "",
        latitude: r.data.latitude || "",
        longitude: r.data.longitude || "",
        vehicle_access: r.data.vehicle_access || false,
        vehicle_type: r.data.vehicle_type || "",
        primary_waste_types: r.data.primary_waste_types || [],
        auto_schedule_frequency: r.data.auto_schedule_frequency || "on_demand",
      });
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  // Iter 73 — D-BMW waste-type catalogue for the "primary waste" chips.
  // Uses the customer-scoped `/me/waste-categories` endpoint so we
  // respect tenant isolation. Falls back silently if unreachable.
  const [primaryOptions, setPrimaryOptions] = useState([]);
  useEffect(() => {
    custApi.get("/me/waste-categories")
      .then(r => {
        const bmw = (r.data || []).find(c => c.category === "D-BMW");
        setPrimaryOptions(bmw?.types || []);
      })
      .catch(() => {});
  }, []);
  const togglePrimaryWaste = (id) => setForm(s => ({
    ...s,
    primary_waste_types: (s.primary_waste_types || []).includes(id)
      ? s.primary_waste_types.filter(x => x !== id)
      : [...(s.primary_waste_types || []), id],
  }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      payload.latitude = payload.latitude === "" ? null : Number(payload.latitude);
      payload.longitude = payload.longitude === "" ? null : Number(payload.longitude);
      await custApi.put("/me", payload);
      toast.success("Profile updated");
      setEditing(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const useGps = () => {
    if (!navigator.geolocation) return toast.error("GPS not supported");
    navigator.geolocation.getCurrentPosition(
      pos => {
        setForm(s => ({ ...s, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) }));
        toast.success("Location captured");
      },
      err => toast.error("GPS denied: " + err.message),
    );
  };

  if (!me) return <div className="p-8 text-center text-sm text-[#5C5A55]" data-testid="mp-loading">Loading…</div>;

  return (
    <div className="font-body px-4 py-4 space-y-4 pb-4" data-testid="me-profile">
      {/* Hero card */}
      <div className="bg-gradient-to-br from-[#DC2626] to-[#7F1D1D] text-white rounded-3xl p-5 shadow-[0_20px_50px_rgba(220,38,38,0.20)] relative overflow-hidden">
        <div className="absolute -top-12 -right-8 w-40 h-40 rounded-full bg-white/10 blur-2xl"/>
        <div className="relative">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] opacity-90 font-bold">
            <User size={11} weight="fill"/> My Profile
          </div>
          <h1 className="font-display font-black text-2xl mt-1 tracking-tight">{me.name}</h1>
          <div className="text-xs opacity-90 mt-1 inline-flex items-center gap-2">
            <span className="font-mono">{me.customer_code || "—"}</span>
            {me.status === "active" && (
              <span className="inline-flex items-center gap-1 bg-white/15 rounded-full px-2 py-0.5">
                <CheckCircle size={10} weight="fill"/>{me.status}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-2">
        {!editing ? (
          <button onClick={() => setEditing(true)} data-testid="mp-edit"
                  className="h-10 px-3 inline-flex items-center gap-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-medium">
            <PencilSimple size={14}/>Edit profile
          </button>
        ) : (
          <>
            <button onClick={save} disabled={busy} data-testid="mp-save"
                    className="h-10 px-3 inline-flex items-center gap-2 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded-md text-sm font-medium">
              <FloppyDisk size={14}/>{busy ? "Saving…" : "Save"}
            </button>
            <button onClick={() => { setEditing(false); load(); }} data-testid="mp-cancel"
                    className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm">
              <X size={14}/>Cancel
            </button>
          </>
        )}
        <button onClick={logout} data-testid="mp-logout"
                className="ml-auto h-10 px-3 text-xs text-[#7A2E18] hover:underline">
          Logout
        </button>
      </div>

      {/* Iter 138 — Security (passcode) */}
      <SecuritySection phone={me.phone}/>

      {/* Iter 154 — Health Profile: loved ones + conditions */}
      <HealthProfileSection/>

      {/* Iter 73 — Your primary waste (D-BMW) — sits above Personal so
          the customer can update their declared waste in one tap. */}
      <div className="bg-[#EFF3EE] border border-[#FEE2E2] rounded-md p-4"
            data-testid="mp-primary-waste-section">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#DC2626] font-semibold">
          Your primary waste
        </div>
        <div className="text-[12px] text-[#5C5A55] mt-0.5">
          {editing
            ? "Tap the items you regularly dispose of. Multi-select."
            : "Items you regularly dispose of."}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {primaryOptions.length === 0 && (
            <div className="text-[11px] text-[#8A8780] italic">
              {editing ? "Loading options…" : "No selection yet."}
            </div>
          )}
          {primaryOptions.map(o => {
            const arr = (editing ? form.primary_waste_types : me.primary_waste_types) || [];
            const on = arr.includes(o.id);
            if (!editing && !on) return null;  // read-only: show only selected chips
            return editing ? (
              <button key={o.id} type="button"
                      onClick={() => togglePrimaryWaste(o.id)}
                      data-testid={`mp-primary-waste-${o.id}`}
                      aria-pressed={on}
                      className={`h-9 px-3 rounded-full border text-xs font-medium transition-colors ${
                        on
                          ? "bg-[#DC2626] text-white border-[#DC2626]"
                          : "bg-white text-[#1A1A1A] border-[#E5E2D9] hover:border-[#DC2626]"
                      }`}>
                {on ? "✓ " : ""}{o.name}
              </button>
            ) : (
              <span key={o.id}
                    data-testid={`mp-primary-waste-chip-${o.id}`}
                    className="h-9 px-3 inline-flex items-center rounded-full bg-white border border-[#FEE2E2] text-xs text-[#DC2626] font-medium">
                ✓ {o.name}
              </span>
            );
          })}
          {!editing && (me.primary_waste_types || []).length === 0 && primaryOptions.length > 0 && (
            <button type="button"
                    onClick={() => setEditing(true)}
                    data-testid="mp-primary-waste-add"
                    className="h-9 px-3 rounded-full border border-dashed border-[#DC2626] text-xs text-[#DC2626] font-medium hover:bg-white">
              + Add your primary waste
            </button>
          )}
        </div>
      </div>

      {/* Personal */}
      <Section title="Personal" icon={User}>
        <Field label="Full name" icon={User}>
          {editing ? (
            <input value={form.name} onChange={e => setForm(s => ({ ...s, name: e.target.value }))}
                   data-testid="mp-name" className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          ) : me.name}
        </Field>
        <Field label="Phone (login)" icon={Phone} hint="Contact admin to change">
          <span className="font-mono text-sm text-[#5C5A55]">{me.phone}</span>
        </Field>
        <Field label="Alternate phone" icon={Phone}>
          {editing ? (
            <input value={form.alternate_phone} onChange={e => setForm(s => ({ ...s, alternate_phone: e.target.value }))}
                   data-testid="mp-alt-phone" placeholder="Optional"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono"/>
          ) : (me.alternate_phone || "—")}
        </Field>
        <Field label="Email" icon={Envelope}>
          {editing ? (
            <input type="email" value={form.email} onChange={e => setForm(s => ({ ...s, email: e.target.value }))}
                   data-testid="mp-email"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          ) : (me.email || "—")}
        </Field>
      </Section>

      {/* Address */}
      <Section title="Address" icon={House}>
        <Field label="Address" icon={MapPin}>
          {editing ? (
            <textarea rows={2} value={form.address} onChange={e => setForm(s => ({ ...s, address: e.target.value }))}
                      data-testid="mp-address"
                      className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
          ) : (me.address || "—")}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="House #">
            {editing ? (
              <input value={form.house_no} onChange={e => setForm(s => ({ ...s, house_no: e.target.value }))}
                     data-testid="mp-house-no"
                     className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
            ) : (me.house_no || "—")}
          </Field>
          <Field label="House name">
            {editing ? (
              <input value={form.house_name} onChange={e => setForm(s => ({ ...s, house_name: e.target.value }))}
                     data-testid="mp-house-name"
                     className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
            ) : (me.house_name || "—")}
          </Field>
        </div>
        <Field label="Landmark">
          {editing ? (
            <input value={form.landmark} onChange={e => setForm(s => ({ ...s, landmark: e.target.value }))}
                   data-testid="mp-landmark"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          ) : (me.landmark || "—")}
        </Field>
        <Field label="GPS location">
          {editing ? (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input value={form.latitude} onChange={e => setForm(s => ({ ...s, latitude: e.target.value }))}
                       placeholder="Latitude" data-testid="mp-lat"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono"/>
                <input value={form.longitude} onChange={e => setForm(s => ({ ...s, longitude: e.target.value }))}
                       placeholder="Longitude" data-testid="mp-lng"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono"/>
              </div>
              <button onClick={useGps} type="button" data-testid="mp-gps"
                      className="text-xs text-[#DC2626] hover:underline inline-flex items-center gap-1">
                <MapPin size={12}/>Use my current location
              </button>
            </div>
          ) : (me.latitude && me.longitude
              ? <a href={`https://www.google.com/maps?q=${me.latitude},${me.longitude}`}
                    target="_blank" rel="noopener" className="text-[#DC2626] hover:underline font-mono text-xs inline-flex items-center gap-1">
                  {Number(me.latitude).toFixed(5)}, {Number(me.longitude).toFixed(5)} <ArrowSquareOut size={11}/>
                </a>
              : "—")}
        </Field>
      </Section>

      {/* Service zone (read-only — admin controls) */}
      <Section title="Health Zone & LSGD" icon={Buildings} subtitle="Managed by admin — contact support to change">
        <Field label="State"><span>{me.state_name || "—"}</span></Field>
        <Field label="District"><span>{me.district_name || "—"}</span></Field>
        <Field label="LSGD"><span>{me.lsgd_name || "—"}</span></Field>
        <Field label="Ward"><span>{me.ward_name || "—"}</span></Field>
        <Field label="Health zone">
          {me.zone_name ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] bg-[#FEE2E2] text-[#DC2626]">
              {me.zone_name}
            </span>
          ) : <span className="text-[10px] text-[#7A2E18] bg-[#F4C7B9] px-2 py-0.5 rounded uppercase tracking-wider">No zone mapped</span>}
        </Field>
      </Section>

      {/* Service preferences */}
      <Section title="Service" icon={Calendar}>
        <Field label="Customer type" icon={Tag}>
          <span className="capitalize">{me.customer_type?.replace(/_/g, " ") || "—"}</span>
        </Field>
        {/* Iter 76 — Pickup schedule (auto-schedule preference) replaces
            the old service_frequency read-only field. Tile grid renders
            full-width across the two-column Service section. */}
        <Field label="Pickup schedule" fullWidth>
          <div className="grid grid-cols-2 gap-2 mt-1" data-testid="mp-auto-schedule-section">
            {[
              { id: "on_demand", label: "On-demand only", hint: "I'll request when I need it" },
              { id: "weekly", label: "Every week", hint: "Auto-book every 7 days" },
              { id: "alt_weekly", label: "Alternate week", hint: "Auto-book every 14 days" },
              { id: "monthly", label: "Monthly once", hint: "Auto-book every 30 days" },
            ].map((o) => {
              const current = (editing
                ? form.auto_schedule_frequency
                : me.auto_schedule_frequency) || "on_demand";
              const on = current === o.id;
              if (!editing && !on) return null;
              return editing ? (
                <button key={o.id} type="button"
                        onClick={() => setForm((s) => ({ ...s, auto_schedule_frequency: o.id }))}
                        data-testid={`mp-auto-schedule-${o.id}`}
                        aria-pressed={on}
                        className={`text-left rounded-md border p-3 transition-colors ${
                          on
                            ? "bg-[#0D47A1] text-white border-[#0D47A1]"
                            : "bg-white text-[#1A1A1A] border-[#E5E2D9] hover:border-[#0D47A1]"
                        }`}>
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    {on ? "✓ " : ""}{o.label}
                  </div>
                  <div className={`text-[10px] mt-0.5 ${on ? "opacity-80" : "text-[#8A8780]"}`}>
                    {o.hint}
                  </div>
                </button>
              ) : (
                <div key={o.id}
                      data-testid={`mp-auto-schedule-chip-${o.id}`}
                      className="text-left rounded-md border border-[#BBDEFB] bg-white p-3">
                  <div className="text-sm font-semibold text-[#0D47A1]">✓ {o.label}</div>
                  <div className="text-[10px] mt-0.5 text-[#5C5A55]">{o.hint}</div>
                </div>
              );
            })}
          </div>
        </Field>
        <Field label="Service status">
          <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider ${
            me.service_status === "active" ? "bg-[#FEE2E2] text-[#DC2626]" :
            me.service_status === "stopped" ? "bg-[#F4C7B9] text-[#7A2E18]" :
            "bg-[#F9E4B7] text-[#7A5A11]"
          }`}>{(me.service_status || "active").replace(/_/g, " ")}</span>
        </Field>
        <Field label="Vehicle access" icon={Truck}>
          {editing ? (
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={form.vehicle_access}
                     onChange={e => setForm(s => ({ ...s, vehicle_access: e.target.checked }))}
                     data-testid="mp-veh-acc" className="accent-[#DC2626]"/>
              <span className="text-sm">Yes, vehicle can reach my address</span>
            </label>
          ) : (me.vehicle_access ? "Yes" : "No")}
        </Field>
        {(editing || me.vehicle_type) && (
          <Field label="Vehicle type">
            {editing ? (
              <select value={form.vehicle_type}
                       onChange={e => setForm(s => ({ ...s, vehicle_type: e.target.value }))}
                       data-testid="mp-veh-type"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
                {VEHICLE_TYPES.map(v => <option key={v} value={v}>{v ? v.replace(/_/g, " ") : "Select…"}</option>)}
              </select>
            ) : (me.vehicle_type ? me.vehicle_type.replace(/_/g, " ") : "—")}
          </Field>
        )}
      </Section>

      {/* Compliance */}
      <Section title="Compliance" icon={IdentificationCard}>
        <Field label="Haritha Mithram ID">
          {editing ? (
            <input value={form.haritha_mithram_id} onChange={e => setForm(s => ({ ...s, haritha_mithram_id: e.target.value }))}
                   data-testid="mp-hm-id" placeholder="Optional"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono"/>
          ) : (me.haritha_mithram_id || "—")}
        </Field>
        <Field label="Member since" icon={Leaf}>
          <span className="text-[#5C5A55]">{me.created_at ? new Date(me.created_at).toLocaleDateString(undefined, {year:'numeric', month:'long', day:'numeric'}) : "—"}</span>
        </Field>
      </Section>
    </div>
  );
}

const Section = ({ title, subtitle, icon: I, children }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
    <div className="px-4 py-2.5 border-b border-[#E5E2D9] flex items-center gap-2">
      {I && <I size={13} className="text-[#DC2626]"/>}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">{title}</div>
        {subtitle && <div className="text-[10px] text-[#8A8780]">{subtitle}</div>}
      </div>
    </div>
    <div className="divide-y divide-[#F0ECE3]">{children}</div>
  </div>
);

const Field = ({ label, icon: I, hint, fullWidth, children }) => (
  <div className={`px-4 py-3 ${fullWidth ? "col-span-2" : ""}`}>
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] inline-flex items-center gap-1 mb-1">
      {I && <I size={10}/>}{label}
      {hint && <span className="ml-auto text-[9px] text-[#8A8780] opacity-70">{hint}</span>}
    </div>
    <div className="text-sm">{children}</div>
  </div>
);



// ─────────────────────────────────────────────────────────────
// Iter 138 — Security section: set / reset the 6-digit passcode.
// Two flows:
//   • Set / change  → uses authenticated POST /me/pin/set
//   • Forgot        → uses POST /public/customer/pin/forgot (email OTP)
//                     then POST /public/customer/login/pin/reset
// ─────────────────────────────────────────────────────────────
function SecuritySection({ phone }) {
  const [mode, setMode] = useState("idle");   // idle | set | forgot
  const [busy, setBusy] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [emailHint, setEmailHint] = useState("");

  const reset = () => {
    setMode("idle");
    setNewPin(""); setConfirmPin(""); setOtp("");
    setDevOtp(""); setEmailHint("");
  };

  const validatePin = (pin) => {
    if (pin.length !== 6) return "Passcode must be exactly 6 digits";
    if (/^(\d)\1{5}$/.test(pin)) return "Please pick a less predictable passcode";
    if (pin === "123456" || pin === "654321") return "Please pick a less predictable passcode";
    return null;
  };

  const savePin = async () => {
    const err = validatePin(newPin);
    if (err) return toast.error(err);
    if (newPin !== confirmPin) return toast.error("Both passcodes must match");
    setBusy(true);
    try {
      await custApi.post("/me/pin/set", { new_pin: newPin });
      toast.success("Passcode saved");
      reset();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const startForgot = async () => {
    if (!phone) return toast.error("Phone number missing on profile");
    setBusy(true);
    setMode("forgot");
    try {
      const r = await custApi.post("/public/customer/pin/forgot", { phone });
      setDevOtp(r.data.dev_otp || "");
      setEmailHint(r.data.email_masked || "");
      toast.success(
        r.data.dev_otp
          ? `Demo OTP: ${r.data.dev_otp}`
          : r.data.email_masked
            ? `Reset code sent to ${r.data.email_masked}`
            : "Reset code sent",
      );
    } catch (e) {
      toast.error(formatErr(e));
      setMode("idle");
    }
    setBusy(false);
  };

  const submitForgot = async () => {
    if (otp.length !== 6) return toast.error("Enter the 6-digit reset code");
    const err = validatePin(newPin);
    if (err) return toast.error(err);
    if (newPin !== confirmPin) return toast.error("Both passcodes must match");
    setBusy(true);
    try {
      await custApi.post("/public/customer/login/pin/reset", {
        phone, otp, new_pin: newPin,
      });
      toast.success("Passcode reset");
      reset();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-4"
         data-testid="mp-security-section">
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55] font-semibold">
          Security · Login passcode
        </div>
        <div className="text-[12px] text-[#5C5A55] mt-0.5">
          Your 6-digit passcode is used to log in. Reset anytime with an OTP sent to your email.
        </div>
      </div>
      {mode === "idle" && (
        <div className="grid grid-cols-2 gap-2" data-testid="mp-sec-actions">
          <button data-testid="mp-sec-change"
                  onClick={() => setMode("set")}
                  className="h-11 w-full inline-flex items-center justify-center bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-medium">
            Change passcode
          </button>
          <button data-testid="mp-sec-forgot"
                  onClick={startForgot}
                  disabled={busy}
                  className="h-11 w-full inline-flex items-center justify-center border-2 border-[#DC2626] text-[#DC2626] hover:bg-[#FEE2E2] disabled:opacity-50 rounded-md text-sm font-medium">
            Forgot / Reset OTP
          </button>
        </div>
      )}

      {mode === "set" && (
        <div className="mt-3 p-3 bg-[#FAFAF7] border border-[#E5E2D9] rounded-md space-y-3"
             data-testid="mp-sec-set-block">
          <div className="text-[11px] text-[#5C5A55]">
            Enter a new 6-digit passcode. This will replace your current one immediately.
          </div>
          <input type="password" inputMode="numeric" maxLength={6}
                 value={newPin}
                 onChange={e => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                 placeholder="••••••"
                 data-testid="mp-sec-new-pin"
                 className="w-full h-14 px-4 border border-[#E5E2D9] rounded-md font-mono text-2xl tracking-[0.4em] text-center focus:outline-none focus:border-[#DC2626]"/>
          <input type="password" inputMode="numeric" maxLength={6}
                 value={confirmPin}
                 onChange={e => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                 placeholder="Confirm"
                 data-testid="mp-sec-confirm-pin"
                 className="w-full h-14 px-4 border border-[#E5E2D9] rounded-md font-mono text-2xl tracking-[0.4em] text-center focus:outline-none focus:border-[#DC2626]"/>
          <div className="flex gap-2">
            <button onClick={savePin} disabled={busy || newPin.length !== 6 || newPin !== confirmPin}
                    data-testid="mp-sec-save"
                    className="flex-1 h-10 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded-md text-sm font-medium">
              {busy ? "Saving…" : "Save passcode"}
            </button>
            <button onClick={reset}
                    className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          </div>
        </div>
      )}

      {mode === "forgot" && (
        <div className="mt-3 p-3 bg-[#FAFAF7] border border-[#E5E2D9] rounded-md space-y-3"
             data-testid="mp-sec-forgot-block">
          {devOtp ? (
            <div className="p-2 rounded-md bg-[#F9E4B7] border border-[#E5C978] text-center">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#7A5A11] font-semibold">Demo OTP</div>
              <div className="mt-1 font-mono text-2xl tracking-[0.4em] text-[#7A5A11] font-bold">{devOtp}</div>
            </div>
          ) : emailHint ? (
            <div className="text-[11px] text-[#DC2626] bg-[#EFF3EE] border border-[#FEE2E2] rounded p-2 text-center">
              Reset code sent to <b>{emailHint}</b>
            </div>
          ) : null}
          <input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                 inputMode="numeric" maxLength={6} placeholder="6-digit reset code"
                 data-testid="mp-sec-otp"
                 className="w-full h-14 px-4 border border-[#E5E2D9] rounded-md font-mono text-2xl tracking-[0.4em] text-center focus:outline-none focus:border-[#DC2626]"/>
          <input type="password" inputMode="numeric" maxLength={6}
                 value={newPin}
                 onChange={e => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                 placeholder="New passcode"
                 data-testid="mp-sec-forgot-new"
                 className="w-full h-14 px-4 border border-[#E5E2D9] rounded-md font-mono text-2xl tracking-[0.4em] text-center focus:outline-none focus:border-[#DC2626]"/>
          <input type="password" inputMode="numeric" maxLength={6}
                 value={confirmPin}
                 onChange={e => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                 placeholder="Confirm passcode"
                 data-testid="mp-sec-forgot-confirm"
                 className="w-full h-14 px-4 border border-[#E5E2D9] rounded-md font-mono text-2xl tracking-[0.4em] text-center focus:outline-none focus:border-[#DC2626]"/>
          <div className="flex gap-2">
            <button onClick={submitForgot}
                    disabled={busy || otp.length !== 6 || newPin.length !== 6 || newPin !== confirmPin}
                    data-testid="mp-sec-forgot-submit"
                    className="flex-1 h-10 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded-md text-sm font-medium">
              {busy ? "Resetting…" : "Reset passcode"}
            </button>
            <button onClick={reset}
                    className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Iter 154 — Health Profile · Loved ones + conditions
// Lets the customer add family members with age / relation / diseases.
// These are shown on the Health tab so vaccine + pill reminders can
// be attached to a specific person.
// ─────────────────────────────────────────────────────────────
const RELATIONS = [
  "son", "daughter", "wife", "husband", "mother", "father",
  "brother", "sister", "grandfather", "grandmother", "self", "other",
];
const COMMON_DISEASES = ["BP", "Diabetic", "Cardiac", "Asthma", "Thyroid", "Cholesterol"];
// Iter 155 Phase 1 — Care Kind unlocks the right daily tracker on Health tab.
const CARE_KINDS = [
  { v: "self",     l: "Self",     hint: "You" },
  { v: "baby",     l: "Baby",     hint: "0–5 yr" },
  { v: "kid",      l: "Kid",      hint: "5–18 yr" },
  { v: "elder",    l: "Elder",    hint: "Parents / grandparents" },
  { v: "woman",    l: "Woman",    hint: "Menstrual / wellness" },
  { v: "pregnant", l: "Pregnant", hint: "Pregnancy journey" },
  { v: "pet",      l: "Pet",      hint: "Dog / cat" },
];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
function ageFromDOB(dob) {
  if (!dob) return "";
  const d0 = new Date(dob + "T00:00:00"); const now = new Date();
  const y = now.getFullYear() - d0.getFullYear();
  return String(Math.max(0, (now.getMonth() > d0.getMonth() || (now.getMonth() === d0.getMonth() && now.getDate() >= d0.getDate())) ? y : y - 1));
}
function suggestKind(dob, currentKind) {
  if (!dob || currentKind === "pet" || currentKind === "pregnant" || currentKind === "woman") return currentKind;
  const age = Number(ageFromDOB(dob));
  if (age >= 60) return "elder";
  if (age >= 5) return "kid";
  if (age < 5) return "baby";
  return currentKind;
}

function HealthProfileSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // "new" | <id> | null
  const [form, setForm] = useState(blankForm());

  function blankForm() {
    return { name: "", age: "", relation: "son", kind: "self", diseases: [], notes: "",
             date_of_birth: "", lmp_date: "", gender: "", blood_group: "",
             pet_species: "dog", breed: "", microchip: "", insurance_provider: "", insurance_expiry: "" };
  }

  const load = async () => {
    try {
      const r = await custApi.get("/me/loved-ones");
      setItems(r.data.loved_ones || []);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggleDisease = (d) => setForm(s => ({
    ...s,
    diseases: s.diseases.includes(d)
      ? s.diseases.filter(x => x !== d)
      : [...s.diseases, d],
  }));

  const startNew = () => { setForm(blankForm()); setEditing("new"); };
  const startEdit = (row) => {
    setForm({
      name: row.name || "",
      age: row.age == null ? "" : String(row.age),
      relation: row.relation || "son",
      kind: row.kind || "self",
      diseases: row.diseases || [],
      notes: row.notes || "",
      date_of_birth: row.date_of_birth || "",
      lmp_date: row.lmp_date || "",
      gender: row.gender || "",
      blood_group: row.blood_group || "",
      pet_species: row.pet_species || "dog",
      breed: row.breed || "",
      microchip: row.microchip || "",
      insurance_provider: row.insurance_provider || "",
      insurance_expiry: row.insurance_expiry || "",
    });
    setEditing(row.id);
  };
  const cancel = () => { setEditing(null); setForm(blankForm()); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const payload = {
      name: form.name.trim(),
      age: form.age === "" ? null : Number(form.age),
      relation: form.relation,
      kind: form.kind || "self",
      diseases: form.diseases,
      notes: form.notes.trim() || null,
      date_of_birth: form.date_of_birth || null,
      lmp_date: form.lmp_date || null,
      gender: form.gender || null,
      blood_group: form.blood_group || null,
      pet_species: form.kind === "pet" ? (form.pet_species || "dog") : null,
      breed: form.breed || null,
      microchip: form.microchip || null,
      insurance_provider: form.insurance_provider || null,
      insurance_expiry: form.insurance_expiry || null,
    };
    try {
      if (editing === "new") {
        await custApi.post("/me/loved-ones", payload);
        toast.success("Added");
      } else {
        await custApi.put(`/me/loved-ones/${editing}`, payload);
        toast.success("Updated");
      }
      cancel();
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this person and their reminders?")) return;
    try {
      await custApi.delete(`/me/loved-ones/${id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-4"
         data-testid="mp-health-profile-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#DC2626] font-semibold inline-flex items-center gap-1.5">
            <Heart size={11} weight="fill"/> Health Profile · Loved ones
          </div>
          <div className="text-[12px] text-[#5C5A55] mt-0.5">
            Family members you care for. Add ages &amp; conditions so we can help with vaccines and pill reminders.
          </div>
        </div>
        {!editing && (
          <button onClick={startNew} data-testid="mp-lo-add"
                  className="h-9 px-3 inline-flex items-center gap-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-medium">
            <Plus size={12} weight="bold"/> Add
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-[#8A8780] py-4 text-center">Loading…</div>
      ) : (
        <div className="space-y-2" data-testid="mp-lo-list">
          {editing === "new" && (
            <div className="border-2 border-dashed border-[#DC2626] rounded-md p-3 bg-[#FEF7F6]"
                 data-testid="mp-lo-new-form">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#DC2626] font-bold mb-2 inline-flex items-center gap-1.5">
                <Plus size={11} weight="bold"/> Add loved one
              </div>
              <EditForm form={form} setForm={setForm}
                        toggleDisease={toggleDisease}
                        onSave={save} onCancel={cancel}/>
            </div>
          )}
          {items.length === 0 && !editing && (
            <div className="text-xs text-[#8A8780] py-3 text-center italic">
              No loved ones added yet. Tap <b>Add</b> to include your family.
            </div>
          )}
          {items.map(row => (
            <div key={row.id}
                 data-testid={`mp-lo-row-${row.id}`}
                 className="border border-[#E5E2D9] rounded-md p-3 hover:border-[#DC2626] transition-colors">
              {editing === row.id ? (
                <EditForm form={form} setForm={setForm}
                          toggleDisease={toggleDisease}
                          onSave={save} onCancel={cancel}/>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#FEE2E2] text-[#DC2626] inline-flex items-center justify-center text-sm font-bold shrink-0">
                    {row.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[#1A1A1A] truncate">
                      {row.name}
                      <span className="ml-1.5 text-[10px] uppercase tracking-widest text-[#5C5A55] font-normal">
                        {row.relation}{row.age != null ? ` · ${row.age}y` : ""}
                      </span>
                    </div>
                    {(row.diseases || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {row.diseases.map(d => (
                          <span key={d} className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#7F1D1D] font-medium">
                            {d}
                          </span>
                        ))}
                      </div>
                    )}
                    {row.notes && (
                      <div className="text-[11px] text-[#5C5A55] mt-1 line-clamp-2">{row.notes}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button onClick={() => startEdit(row)}
                            data-testid={`mp-lo-edit-${row.id}`}
                            className="p-1.5 rounded hover:bg-[#F7F6F2]">
                      <PencilLine size={14} color="#5C5A55"/>
                    </button>
                    <button onClick={() => remove(row.id)}
                            data-testid={`mp-lo-delete-${row.id}`}
                            className="p-1.5 rounded hover:bg-[#FEE2E2]">
                      <Trash size={14} color="#DC2626"/>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditForm({ form, setForm, toggleDisease, onSave, onCancel }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Name *</label>
          <input value={form.name}
                 onChange={e => setForm(s => ({ ...s, name: e.target.value }))}
                 data-testid="mp-lo-name"
                 placeholder="e.g. Aarav"
                 className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Date of birth</label>
          <input type="date" value={form.date_of_birth || ""}
                 onChange={e => {
                   const dob = e.target.value;
                   setForm(s => ({
                     ...s,
                     date_of_birth: dob,
                     age: ageFromDOB(dob),
                     kind: suggestKind(dob, s.kind),
                   }));
                 }}
                 data-testid="mp-lo-dob"
                 className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
          {form.age !== "" && (
            <div className="text-[10px] text-[#5C5A55] mt-0.5">Auto age: <b>{form.age}y</b></div>
          )}
        </div>
        <div className="col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Relation</label>
          <select value={form.relation}
                  onChange={e => setForm(s => ({ ...s, relation: e.target.value }))}
                  data-testid="mp-lo-relation"
                  className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm bg-white">
            {RELATIONS.map(r => (
              <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Care kind · unlocks tracker</label>
          <div className="grid grid-cols-3 gap-1.5" data-testid="mp-lo-kind">
            {CARE_KINDS.map(k => {
              const on = k.v === (form.kind || "self");
              return (
                <button key={k.v} type="button"
                        data-testid={`mp-lo-kind-${k.v}`}
                        onClick={() => setForm(s => ({ ...s, kind: k.v }))}
                        className={`h-11 rounded-md text-xs font-semibold border transition-colors flex flex-col items-center justify-center leading-tight ${
                          on ? "bg-[#DC2626] text-white border-[#DC2626]"
                             : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                        }`}>
                  <span>{k.l}</span>
                  <span className={`text-[9px] font-normal ${on ? "opacity-90" : "opacity-60"}`}>{k.hint}</span>
                </button>
              );
            })}
          </div>
        </div>
        {/* Kind-specific fields */}
        <div className="col-span-2 grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Gender</label>
            <select value={form.gender || ""}
                    onChange={e => setForm(s => ({ ...s, gender: e.target.value }))}
                    data-testid="mp-lo-gender"
                    className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm bg-white">
              <option value="">—</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Blood group</label>
            <select value={form.blood_group || ""}
                    onChange={e => setForm(s => ({ ...s, blood_group: e.target.value }))}
                    data-testid="mp-lo-blood"
                    className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm bg-white">
              <option value="">—</option>
              {BLOOD_GROUPS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
        </div>
        {form.kind === "pregnant" && (
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">
              Last period date (LMP) — used to compute week &amp; due date
            </label>
            <input type="date" value={form.lmp_date || ""}
                   onChange={e => setForm(s => ({ ...s, lmp_date: e.target.value }))}
                   data-testid="mp-lo-lmp"
                   className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
          </div>
        )}
        {form.kind === "pet" && (
          <PetSpecificFields form={form} setForm={setForm}/>
        )}
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Any conditions</label>
        <div className="flex flex-wrap gap-1.5">
          {COMMON_DISEASES.map(d => {
            const on = form.diseases.includes(d);
            return (
              <button key={d} type="button"
                      data-testid={`mp-lo-disease-${d.toLowerCase()}`}
                      onClick={() => toggleDisease(d)}
                      className={`h-7 px-2.5 rounded-full text-[11px] font-medium border transition-colors ${
                        on
                          ? "bg-[#DC2626] text-white border-[#DC2626]"
                          : "bg-white text-[#1A1A1A] border-[#E5E2D9] hover:border-[#DC2626]"
                      }`}>
                {on ? "✓ " : ""}{d}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Other details</label>
        <input value={form.notes}
               onChange={e => setForm(s => ({ ...s, notes: e.target.value }))}
               data-testid="mp-lo-notes"
               placeholder="Allergies, blood group, etc."
               className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
      </div>
      <div className="grid grid-cols-2 gap-2 pt-1">
        <button onClick={onSave}
                data-testid="mp-lo-save"
                className="h-9 w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-medium">
          Save
        </button>
        <button onClick={onCancel}
                data-testid="mp-lo-cancel"
                className="h-9 w-full border border-[#E5E2D9] rounded-md text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}

// Iter 160 — Pet species selector + breed dropdown (loaded from backend)
function PetSpecificFields({ form, setForm }) {
  const [breeds, setBreeds] = useState([]);
  const species = form.pet_species || "dog";
  useEffect(() => {
    custApi.get(`/me/breeds?species=${species}`)
      .then(r => setBreeds(r.data.breeds || []))
      .catch(() => setBreeds([]));
  }, [species]);
  return (
    <>
      <div className="col-span-2">
        <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Species</label>
        <div className="grid grid-cols-3 gap-1.5">
          {[{v:"dog", l:"🐶 Dog"},{v:"cat", l:"🐱 Cat"},{v:"other", l:"🐾 Other"}].map(s => {
            const on = s.v === species;
            return (
              <button key={s.v} type="button"
                      onClick={() => setForm(f => ({ ...f, pet_species: s.v, breed: "" }))}
                      data-testid={`mp-lo-species-${s.v}`}
                      className={`h-10 rounded-md text-xs font-semibold border ${
                        on ? "bg-[#0F766E] text-white border-[#0F766E]"
                           : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                      }`}>{s.l}</button>
            );
          })}
        </div>
      </div>
      <div className="col-span-2">
        <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Breed</label>
        {species === "other" ? (
          <input value={form.breed || ""}
                 onChange={e => setForm(s => ({ ...s, breed: e.target.value }))}
                 data-testid="mp-lo-breed"
                 placeholder="Enter breed"
                 className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
        ) : (
          <select value={form.breed || ""}
                  onChange={e => setForm(s => ({ ...s, breed: e.target.value }))}
                  data-testid="mp-lo-breed"
                  className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm bg-white">
            <option value="">Select breed…</option>
            {breeds.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        )}
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Microchip #</label>
        <input value={form.microchip || ""}
               onChange={e => setForm(s => ({ ...s, microchip: e.target.value }))}
               data-testid="mp-lo-microchip"
               placeholder="15-digit"
               className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm font-mono"/>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Insurance</label>
        <input value={form.insurance_provider || ""}
               onChange={e => setForm(s => ({ ...s, insurance_provider: e.target.value }))}
               data-testid="mp-lo-insprov"
               placeholder="Provider"
               className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
      </div>
      <div className="col-span-2">
        <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Insurance expiry</label>
        <input type="date" value={form.insurance_expiry || ""}
               onChange={e => setForm(s => ({ ...s, insurance_expiry: e.target.value }))}
               data-testid="mp-lo-insexp"
               className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
      </div>
    </>
  );
}
